import { Navigate, type RouteObject } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth';
import { AdminLayout } from './features/shell/AdminLayout';
import { LoginPage } from './pages/LoginPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { ApplicationDetailPage } from './pages/ApplicationDetailPage';
import { ProviderHealthBoardPage } from './pages/ProviderHealthBoardPage';
import { ModelsPage } from './pages/ModelsPage';
import { RoutingPolicyEditorPage } from './pages/RoutingPolicyEditorPage';
import { QuotaEditorPage } from './pages/QuotaEditorPage';
import { MonthlyQuotaReportPage } from './pages/MonthlyQuotaReportPage';
import { UsageDashboardPage } from './pages/UsageDashboardPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { DocsPage } from './pages/DocsPage';

// Routed admin shell (C27).
//
//   /login                                              public — operator sign in
//   ── RequireAuth ──────────────────────────────────  everything below redirects to /login
//   /            -> /clients                            when unauthenticated
//   /clients                                            tenancy root
//   /clients/:clientId                                  applications for a client
//   /clients/:clientId/applications/:applicationId      API keys (issue / revoke / rotate / override)
//   /providers                                          provider health board + live health (C28/C29)
//   /policies                                           routing-policy editor (C28)
//   /quotas                                             quota editor (C28)
//   /usage                                              live usage dashboard (C29)
//   /audit                                              audit-log viewer (C29)
//   /docs                                               in-SPA consumer docs + live availability (W4)
//
// The route tree is exported as a data-router config (RouteObject[]) and mounted in main.tsx via
// createBrowserRouter + RouterProvider. A data router is required so navigation-blocking hooks
// (useBlocker — used to protect the one-time rawKey reveal on the application keys page) work.
// The Theme/Auth providers wrap the RouterProvider in main.tsx, so this module is just the route
// table (which keeps it trivially testable under a memory data router).
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/clients" replace /> },
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/:clientId', element: <ClientDetailPage /> },
          {
            path: 'clients/:clientId/applications/:applicationId',
            element: <ApplicationDetailPage />,
          },
          { path: 'providers', element: <ProviderHealthBoardPage /> },
          { path: 'models', element: <ModelsPage /> },
          { path: 'policies', element: <RoutingPolicyEditorPage /> },
          { path: 'quotas', element: <QuotaEditorPage /> },
          { path: 'reports/quotas', element: <MonthlyQuotaReportPage /> },
          { path: 'usage', element: <UsageDashboardPage /> },
          { path: 'audit', element: <AuditLogPage /> },
          { path: 'docs', element: <DocsPage /> },
        ],
      },
    ],
  },
  // Unknown routes fall back to the app root (which itself gates on auth).
  { path: '*', element: <Navigate to="/" replace /> },
];
